(ns frontend.view.entity-viewers.utils
  (:require [frontend.state.current-session :as session]
            [frontend.logging :as logging]
            [frontend.updates.entity-scope :as entity-scope]
            [frontend.utils :as fu]
            [reagent.impl.component :as reagent-component]
            [reagent.core :as r]
            [common.features.plan :as plan]))

(def ^:private entity-scope-instance-id
  :nev-entity-viewer)

(defn- entity-loaded?
  [entity-id]
  (some? (:entity/type (get @session/entities entity-id))))

(defn- merge-entity-scope-upserts!
  [message]
  (let [upserts (->> (:ops message)
                     (filter (fn [op] (= :upsert (:op op))))
                     (mapcat :entities)
                     (remove nil?))]
    (when (seq upserts)
      (fu/manage-entities session/reverse-lookup session/session upserts))))

(defn- entity-available!
  "The entity is in the session cache, so the viewer can render. Clearing
   `:not-found` is the point: it is set on a failed or empty fetch and was
   never removed again, so a stream that recovered - `resubscribe-active-streams!`
   re-subscribes whenever the socket reopens - cleared `:loading` and left the
   viewer showing \"This page is not available\" anyway."
  [state]
  (swap! state (fn [current]
                 (dissoc current :loading :not-found))))

(defn- handle-entity-scope-message!
  [state entity-id message]
  (merge-entity-scope-upserts! message)
  (if (entity-loaded? entity-id)
    (entity-available! state)
    (swap! state assoc :not-found true)))

;; The subscribe RPC reply wraps the initial snapshot as {:body {:initial-message
;; ...}}, one level deeper than frontend.updates' own on-message delivery expects
;; (see its send-subscribe!) - unwrap-body/on-open mirror ui.data.view-entities.event's
;; existing workaround for the same shape mismatch.
(defn- unwrap-body
  [payload]
  (if (and (map? payload)
           (map? (:body payload)))
    (:body payload)
    payload))

;; Not every entity of a synced type reaches the bulk sync eagerly anymore
;; (e.g. backend.domain.subscription.sync skips oppo/opportunity types for
;; new-grid users) - fetch on demand and merge into the same cache instead.
(defn- fetch-entity-on-demand!
  [state entity-id]
  (reset! state {:loading true
                 :entity-scope-entity-id entity-id})
  (entity-scope/open!
   {:entity-id entity-id
    :instance-id entity-scope-instance-id
    :on-open (fn [response]
               (when-let [initial-message (:initial-message (unwrap-body response))]
                 (handle-entity-scope-message! state entity-id initial-message)))
    :on-message (fn [message]
                  (handle-entity-scope-message! state entity-id message))
    :on-error (fn [error]
                (logging/error "Entity scope subscription failed in entity-viewer : "
                               entity-id " >> " error)
                ;; A failed subscription is not evidence that the entity is
                ;; missing. When it is already in the session cache - the
                ;; bulk-synced case, i.e. anything outside new-grid's narrowed
                ;; sync - render it rather than claiming the page does not
                ;; exist; only the on-demand reverse-reference modules are
                ;; affected, and they degrade to empty on their own.
                (if (entity-loaded? entity-id)
                  (entity-available! state)
                  (swap! state assoc :not-found true)))}))

(defn reset-state!
  "`entity` already present in `@session/entities` (a bulk-synced type, e.g.
  most business-record types for new-grid-2023 accounts - see
  backend.domain.subscription.sync/new-grid-generic-sync-entity-types) does
  NOT mean this entity's on-demand reverse-pointer graph
  (entity-scope/open!, the only thing that populates e.g. a fund's
  fund-commitments/holdings/fundraising-opportunities via their own
  :fund-commitment/fund-shaped reverse refs) has ever been fetched - those
  two things were conflated here, silently leaving such NEV modules empty
  with no error (see ai/mistakes.md). Pass {:always-fetch? true} for an
  entity type whose NEV has reverse-pointer-driven modules but is also
  bulk-synced, to force the on-demand fetch regardless."
  ([state entity-id] (reset-state! state entity-id nil))
  ([state entity-id {:keys [always-fetch?]}]
   (if (or (not entity-id)
           (js/isNaN entity-id))
     (do
       (logging/error "No entity id in entity-viewer : " entity-id " >> " @state)
       (swap! state assoc :not-found true))
     (let [entity (get @session/entities entity-id)]
       (if (and entity (not always-fetch?))
         (reset! state {})
         (fetch-entity-on-demand! state entity-id))))))

(defonce force-remount-atom (r/atom 0))

(defn force-remount! []
  (swap! force-remount-atom inc))

(defn component-did-update [id-attribute reset-view-state! this old-argv]
  (let [prev-id (-> old-argv
                    reagent-component/extract-props
                    id-attribute)
        new-id (-> this
                   r/props
                   id-attribute)]
    (when-not (= prev-id new-id)
      (reset-view-state!)
      (force-remount!))))

(defn clear-state! [state]
  (when-let [entity-id (:entity-scope-entity-id @state)]
    (entity-scope/close! entity-id entity-scope-instance-id))
  (reset! state {:loading true}))

(defn resolve-attachments [entity]
  (let [attachment-ids (:common/attachments entity)
        attachments (->> attachment-ids
                         (map (fn [id]
                                (get @session/entities id))))]
    attachments))

(defn resolve-editor-type [attribute field-schema]
  (if (or (and (session/is-guest?)
               (some #{attribute} [:common/related-entities
                                   :common/related-entities-oppo
                                   :common/related-entities-person
                                   :common/related-entities-company
                                   :common/related-entities-investor
                                   :common/related-entities-fund
                                   :common/related-entities-fundraising-opportunities
                                   :common/related-entities-sales-opportunities
                                   :common/related-entities-holdings
                                   :common/related-entities-sale-agreements
                                   :common/related-entities-real-estate
                                   :common/related-entities-fof-asset
                                   :common/related-entities-other-asset
                                   :common/related-entities-fund-commitment]))
          ;; remove if feature not enabled or empty choices
          (and (not (session/is-guest?))
               (case attribute
                 :common/related-entities-fund-commitment
                 (or (not (session/fundraising?))
                     (session/no-known-entities? :entity.type/fund-commitment @session/all-fund-commitments))
                 :common/related-entities-fund
                 (or (not (session/fundraising?))
                     (not (session/has-feature-access? :feature/fund-management))
                     (session/no-known-entities? :entity.type/fund @session/all-funds))
                 :common/related-entities-fundraising-opportunities
                 (or (not (session/fundraising?))
                     (session/no-known-entities? :entity.type/fundraising-opportunity @session/all-fundraising-opportunities))
                 :common/related-entities-sales-opportunities
                 (or (not (session/sales-pipelines?))
                     (session/no-known-entities? :entity.type/sales-opportunity @session/all-sales-opportunities))
                 :common/related-entities-holdings
                 (or (not (session/portfolio?))
                     (session/no-known-entities? :entity.type/holding @session/all-holdings))
                 :common/related-entities-sale-agreements
                 (or (not (session/sales-pipelines?))
                     (session/no-known-entities? :entity.type/sale-agreement @session/all-sale-agreements))
                 :common/related-entities-investor
                 (or (and (not (session/portfolio?))
                          (not (session/fundraising?)))
                     (session/no-known-entities? :entity.type/investor @session/all-investors))
                 :common/related-entities-real-estate
                 (or (not (plan/available :feature/asset-types-real-estate @session/tenant))
                     (session/no-known-entities? :entity.type/real-estate @session/all-real-estates))
                 :common/related-entities-fof-asset
                 (or (not (plan/available :feature/asset-types-fof @session/tenant))
                     (session/no-known-entities? :entity.type/fof-asset @session/all-fof-assets))
                 :common/related-entities-other-asset
                 (or (not (plan/available :feature/asset-types-other @session/tenant))
                     (session/no-known-entities? :entity.type/other-asset @session/all-other-assets))
                 ;;no need for company, oppo, person?
                 false)))
    :editor.type/none
    (->> field-schema :field/default-visibility :editor)))
